import { motion, useReducedMotion } from "framer-motion";
import { useCallback, useMemo, useState } from "react";

const gridClass =
  "grid w-full grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 md:grid-cols-4 md:gap-5 lg:grid-cols-5 xl:grid-cols-6 xl:gap-6";

function formatCategoryCounts(menu, products) {
  const subCount = Array.isArray(menu.items) ? menu.items.length : 0;
  const productCount = products.filter((p) => p.category === menu.title).length;
  const prodLabel = productCount === 1 ? "product" : "products";
  if (subCount === 0) {
    return `${productCount} ${prodLabel} · shop all`;
  }
  const subLabel = subCount === 1 ? "subcategory" : "subcategories";
  return `${subCount} ${subLabel} · ${productCount} ${prodLabel}`;
}

function CategoryCard({
  menu,
  meta,
  imageUrl,
  countLabel,
  selected,
  index,
  reduceMotion,
  onSelect
}) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onPointerMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: py * -8, y: px * 8 });
  }, []);

  const onPointerLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
  }, []);

  const entranceDelay = index * 0.09;
  const entranceDuration = reduceMotion ? 0.2 : 0.55;

  return (
    <motion.div
      className="category-explore-slot relative flex min-h-0 w-full"
      style={{ aspectRatio: "1 / 1" }}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.96 }}
      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
      transition={{
        delay: entranceDelay,
        duration: entranceDuration,
        ease: [0.22, 1, 0.36, 1]
      }}
    >
      <motion.div
        className="flex min-h-0 w-full flex-1"
        animate={reduceMotion ? false : { y: [0, -3, 0] }}
        transition={{
          duration: 4.2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: entranceDelay + entranceDuration
        }}
      >
        <motion.button
          type="button"
          whileHover={reduceMotion ? undefined : { scale: 1.04, y: -4 }}
          whileTap={reduceMotion ? undefined : { scale: 0.98 }}
          onPointerMove={onPointerMove}
          onPointerLeave={onPointerLeave}
          onClick={() => onSelect(menu.title)}
          className={[
            "category-explore-btn-premium category-explore-btn group relative flex aspect-square min-h-0 w-full cursor-pointer flex-col overflow-hidden rounded-[22px] border border-white/15",
            "bg-gradient-to-br from-white/[0.14] to-white/[0.04]",
            "backdrop-blur-xl backdrop-saturate-150",
            "shadow-[0_14px_44px_rgba(2,6,23,0.5)]",
            "transition-[border-color,box-shadow,transform] duration-300 ease-out",
            "hover:border-cyan-400/45 hover:shadow-[0_22px_56px_rgba(56,189,248,0.22),0_0_40px_rgba(99,102,241,0.12)]",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400",
            selected ? "ring-2 ring-cyan-400/90 ring-offset-2 ring-offset-[#0f172a]" : ""
          ].join(" ")}
          aria-pressed={selected}
        >
          {meta?.trending ? <span className="category-explore-ribbon">Trending</span> : null}
          <span
            className="pointer-events-none absolute inset-0 -z-0 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{
              background: "radial-gradient(circle at 50% 38%, rgba(56, 189, 248, 0.22), transparent 58%)"
            }}
          />

          <div
            className="relative z-[1] flex h-full min-h-0 flex-col p-3 transition-transform duration-300 ease-out"
            style={{
              transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
              transformStyle: "preserve-3d"
            }}
          >
            <div className="category-explore-img-wrap flex min-h-0 flex-1 items-center justify-center bg-gradient-to-br from-slate-200/12 via-slate-400/6 to-transparent p-2.5">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={menu.title}
                  className="h-full w-full object-contain object-center"
                  draggable={false}
                  loading={index < 8 ? "eager" : "lazy"}
                  decoding="async"
                />
              ) : (
                <span className="select-none text-4xl leading-none" aria-hidden>
                  {meta?.icon || "🛍️"}
                </span>
              )}
            </div>
            <div className="relative z-[2] mt-auto shrink-0 pt-2 text-center">
              <span className="block font-sans text-sm font-bold tracking-tight text-white md:text-[0.95rem]">{menu.title}</span>
              {meta?.description ? (
                <span className="category-explore-desc">{meta.description}</span>
              ) : null}
              <small className="mt-0.5 block font-sans text-[10px] font-medium leading-snug text-slate-400 sm:text-xs">{countLabel}</small>
            </div>
          </div>

          <div className="category-explore-overlay">
            <span className="category-explore-overlay-text">View Products →</span>
          </div>
        </motion.button>
      </motion.div>

      {!reduceMotion && (
        <motion.span
          className="pointer-events-none absolute inset-0 -z-[1] rounded-[18px]"
          aria-hidden
          animate={{
            boxShadow: [
              "0 0 0 0 rgba(139, 92, 246, 0)",
              "0 0 24px 1px rgba(139, 92, 246, 0.18)",
              "0 0 0 0 rgba(139, 92, 246, 0)"
            ]
          }}
          transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: index * 0.12 + 0.4 }}
        />
      )}
    </motion.div>
  );
}

export default function CategoryExploreGrid({
  categoryMenus,
  categoryMeta,
  products,
  selectedCategory,
  loadingProducts,
  getCategoryPreview,
  onSelectCategory,
  onSelectSubCategory,
  onOpenCategory
}) {
  const reduceMotion = useReducedMotion();
  const prefersReduced = reduceMotion === true;

  const productsList = useMemo(() => (Array.isArray(products) ? products : []), [products]);

  const handleSelect = (title) => {
    onSelectCategory(title);
    onSelectSubCategory("");
    onOpenCategory(title);
  };

  return (
    <div className="category-explore-root relative z-[2] w-full">
      {loadingProducts ? (
        <div className={gridClass}>
          {categoryMenus.map((menu) => (
            <div
              key={`cat-skeleton-${menu.title}`}
              className="category-explore-slot aspect-square w-full min-h-[104px] animate-pulse rounded-[22px] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] shadow-inner"
            >
              <div className="m-3 h-[58%] rounded-xl bg-white/10" />
              <div className="mx-auto mt-4 h-3 w-[60%] rounded-full bg-white/10" />
              <div className="mx-auto mt-2 h-2 w-2/5 rounded-full bg-white/5" />
            </div>
          ))}
        </div>
      ) : (
        <div className={gridClass}>
          {categoryMenus.map((menu, index) => (
            <CategoryCard
              key={menu.title}
              menu={menu}
              meta={categoryMeta[menu.title]}
              imageUrl={getCategoryPreview(menu.title)}
              countLabel={formatCategoryCounts(menu, productsList)}
              selected={selectedCategory === menu.title}
              index={index}
              reduceMotion={prefersReduced}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
